import Box from "@mui/material/Box";
import Loader from "./Display/Loader";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MainCard from "./Layouts/MainCard";
import { useEffect, useState } from "react";
import { Theme } from "../data/style/Theme";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getUserAchievements } from "../api/user";
import { getAchievements } from "../api/helpers";
import {
  SpacedRow,
  MainText,
  SecondaryTitle,
  CenteredRow,
  MainTitle,
  AllCenteredColumn,
  AllCenteredRow,
  scrollBar,
} from "../data/style/Style";

const TIERS = {
  bronze: {
    color: "#cd7c3e",
    glow: "rgba(205,124,62,0.45)",
    bg: "rgba(205,124,62,0.12)",
    border: "rgba(205,124,62,0.35)",
    label: "Bronze",
  },
  silver: {
    color: "#94a3b8",
    glow: "rgba(148,163,184,0.4)",
    bg: "rgba(148,163,184,0.12)",
    border: "rgba(148,163,184,0.3)",
    label: "Silver",
  },
  gold: {
    color: Theme.palette.gold,
    glow: "rgba(250,204,21,0.55)",
    bg: "rgba(250,204,21,0.12)",
    border: "rgba(250,204,21,0.38)",
    label: "Gold",
  },
};

const CATEGORIES = [
  {
    id: 4,
    key: "weightlifting",
    icon: "🏋",
    label: "Weightlifting",
    color: Theme.palette.gold,
  },
  {
    id: 2,
    key: "gymnastics",
    icon: "🤸",
    label: "Gymnastics",
    color: Theme.palette.blue,
  },
  {
    id: 3,
    key: "cardio",
    icon: "🏃",
    label: "Cardio",
    color: Theme.palette.up,
  },
];
const Achievements = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [tierStat, setTierStat] = useState({});
  const [loading, setLoading] = useState(true);

  const { data: achievements, isPending: achievementsPending } = useQuery({
    queryKey: ["achievements", "gender"],
    queryFn: ({ signal }) => getAchievements({ signal }),
  });

  const { data: userAchievements, isPending: userAchievementsPending } =
    useQuery({
      queryKey: ["achievements", "user", user.id],
      queryFn: ({ signal }) => getUserAchievements({ signal }),
    });

  useEffect(() => {
    if (!userAchievementsPending && !achievementsPending) {
      setStats(totalStats(achievements, userAchievements));
      setTierStat({
        bronze: tierStats("bronze", achievements, userAchievements),
        silver: tierStats("silver", achievements, userAchievements),
        gold: tierStats("gold", achievements, userAchievements),
      });
      setLoading(false);
    }
  }, [achievementsPending, userAchievementsPending]);

  return (
    <Stack
      width={"400px"}
      height={"97vh"}
      pr={1}
      overflow={"auto"}
      sx={scrollBar}
      pl={1}
    >
      <Stack
        gap={1}
        p={1}
        sx={{
          border: 1,
          borderColor: Theme.palette.orange,
          background: Theme.palette.orange + "07",
          borderRadius: 4,
        }}
      >
        {!loading ? (
          <Stack gap={2}>
            <SpacedRow alignItems={"center"}>
              <Stack gap={1}>
                <SecondaryTitle
                  sx={{
                    fontWeight: 400 + " !important",
                    color: Theme.palette.orange + " !important",
                  }}
                >
                  Total Progress
                </SecondaryTitle>
                <MainText fontSize={"2rem"} fontWeight={700}>
                  {stats.unlocked}{" "}
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "normal",
                      color: Theme.palette.muted,
                    }}
                  >
                    / {stats.total}
                  </span>
                </MainText>
                <MainText
                  fontSize={"0.9rem"}
                  sx={{ color: Theme.palette.muted + " !important", mt: -2 }}
                >
                  achievements unlocked
                </MainText>
              </Stack>
              <span style={{ fontSize: "3rem" }}>🏅</span>
            </SpacedRow>
            <SpacedRow
              width={"70%"}
              alignSelf={"center"}
              pb={1}
              sx={{ borderBottom: 1, borderColor: Theme.palette.border }}
            >
              {["bronze", "silver", "gold"].map((key) => (
                <Stack key={key} alignItems={"center"}>
                  <MainText
                    fontWeight={700}
                    sx={{ color: TIERS[key].color + " !important" }}
                  >
                    {tierStat[key].unlocked}/{achievements.length}
                  </MainText>
                  <MainText
                    fontSize={"0.7rem"}
                    sx={{ color: Theme.palette.muted + " !important" }}
                  >
                    {TIERS[key].label}
                  </MainText>
                </Stack>
              ))}
            </SpacedRow>
            <Stack width={"70%"} gap={0.5} alignSelf={"center"}>
              <ProgressBar pct={stats.pct} color={Theme.palette.orange} />
              <MainText
                fontSize={"0.7rem"}
                fontWeight={700}
                sx={{
                  color: Theme.palette.orange + " !important",
                  alignSelf: "flex-end",
                }}
              >
                {stats.pct}% Complete
              </MainText>
            </Stack>
          </Stack>
        ) : (
          <Loader minHeight={"150px"} />
        )}
      </Stack>
      <SpacedRow width={"50%"} mt={2} mb={1} alignSelf={"center"}>
        {Object.entries(TIERS).map(([key, t]) => (
          <CenteredRow key={key} gap={1}>
            <Box
              sx={{
                background: t.color,
                width: 10,
                height: 10,
                borderRadius: "50%",
              }}
            />
            <MainText sx={{ color: Theme.palette.muted + " !important" }}>
              {t.label}
            </MainText>
          </CenteredRow>
        ))}
      </SpacedRow>
      {!loading ? (
        <Stack gap={4}>
          {CATEGORIES.map((cat, i) => (
            <CategorySection
              key={i}
              cat={cat}
              exercises={achievements.filter(
                (item) => item.category === cat.id,
              )}
              userAchievements={userAchievements}
            />
          ))}
        </Stack>
      ) : (
        <Loader minHeight={200} />
      )}
    </Stack>
  );
};

export default Achievements;

// HELPERS
function tierStats(tierKey, exercises, userAchievements) {
  let unlocked = 0,
    total = 0;
  exercises.forEach((ex) => {
    const t = ex.tiers.find((tt) => tt.tier === tierKey);
    let pb = 0;
    if (userAchievements.find((item) => item.achievement === ex.id)) {
      pb = userAchievements.find((item) => item.achievement === ex.id).value;
    }
    if (!t) return;
    total++;
    if (isUnlocked(pb, t.value, ex.category === 3)) unlocked++;
  });
  return { unlocked, total };
}

function totalStats(exercises, userAchievements) {
  let total = 0,
    unlocked = 0;
  exercises.forEach((ex) => {
    let pb = 0;
    if (userAchievements.find((item) => item.achievement === ex.id)) {
      pb = userAchievements.find((item) => item.achievement === ex.id).value;
    }
    ex.tiers.forEach((t) => {
      total++;
      if (isUnlocked(pb, t.value, ex.category === 3)) unlocked++;
    });
  });
  return {
    total,
    unlocked,
    pct: total > 0 ? Math.round((unlocked / total) * 100) : 0,
  };
}

function isUnlocked(pb, threshold, lowerBetter = false) {
  if (pb === undefined || pb === 0) return false;
  return lowerBetter ? pb <= threshold : pb >= threshold;
}

const CategorySection = ({ cat, exercises, userAchievements }) => {
  const catUnlocked = exercises.reduce((sum, ex) => {
    let pb = 0;
    if (userAchievements.find((item) => item.achievement === ex.id)) {
      pb = userAchievements.find((item) => item.achievement === ex.id).value;
    }

    return (
      sum +
      ex.tiers.filter((t) => isUnlocked(pb, t.value, ex.category === 3)).length
    );
  }, 0);
  const catTotal = exercises.reduce((s, ex) => s + ex.tiers.length, 0);
  const catPct = catTotal > 0 ? Math.round((catUnlocked / catTotal) * 100) : 0;

  return (
    <Box>
      <MainCard start={cat.color} middle={cat.color}>
        <SpacedRow alignItems={"center"}>
          <CenteredRow gap={1}>
            <MainText fontSize={"1.2rem"}>{cat.icon}</MainText>
            <Stack>
              <MainTitle>{cat.label}</MainTitle>
              <MainText
                fontSize={"0.8rem"}
                sx={{ color: Theme.palette.muted + " !important" }}
              >
                {catUnlocked} of {catTotal} unlocked
              </MainText>
            </Stack>
          </CenteredRow>
          <AllCenteredColumn
            p={0.5}
            sx={{
              border: 2,
              borderColor: cat.color,
              height: 30,
              width: 30,
              borderRadius: "50%",
            }}
          >
            <MainText
              fontWeight={700}
              fontSize={"0.6rem"}
              sx={{ color: cat.color + " !important" }}
            >
              {catPct}%
            </MainText>
          </AllCenteredColumn>
        </SpacedRow>
        {exercises.map((item, index) => (
          <ExerciseRow
            userAchievements={userAchievements}
            key={index}
            exercise={item}
            catColor={cat.color}
          />
        ))}
      </MainCard>
    </Box>
  );
};

const ExerciseRow = ({ exercise, catColor, userAchievements }) => {
  let pb = 0;
  if (userAchievements.find((item) => item.achievement === exercise.id)) {
    pb = userAchievements.find(
      (item) => item.achievement === exercise.id,
    ).value;
  }
  const { pct, nextVal } = progressToNext(
    pb,
    exercise.tiers,
    exercise.category === 3,
  );
  const allDone = pct === 100;
  const unlockedN = exercise.tiers.filter((t) =>
    isUnlocked(pb, t.value, exercise.category === 3),
  ).length;
  let displayValue = false;
  if (exercise.category === 3) {
    const minutes = parseInt(pb / 60);
    const seconds = pb % 60;
    const convertedMinutes = minutes < 10 ? "0" + minutes : minutes;
    const convertedSeconds =
      parseInt(seconds) < 10 ? "0" + parseInt(seconds) : parseInt(seconds);
    displayValue = convertedMinutes + ":" + convertedSeconds;
  }

  return (
    <Stack gap={2} mb={2}>
      <SpacedRow>
        <CenteredRow gap={1}>
          <MainText fontWeight={500}>
            {exercise.category === 3 && exercise.distance + "m "}
            {exercise.exercise_name}
          </MainText>
          <AllCenteredColumn
            p={0.5}
            sx={{
              background: catColor + "07",
              border: 1,
              borderRadius: 2,
              borderColor: catColor + "22",
            }}
          >
            <MainText
              fontSize={"0.7rem"}
              sx={{ color: catColor + " !important" }}
            >
              {unlockedN}/{exercise.tiers.length}
            </MainText>
          </AllCenteredColumn>
        </CenteredRow>
        {pb !== 0 && (
          <MainText
            fontWeight={700}
            fontSize={"0.8rem"}
            sx={{ color: catColor + " !important" }}
          >
            PB: {displayValue || pb}{" "}
            {exercise.category === 3
              ? "min"
              : exercise.category === 2
                ? "reps"
                : "kg"}
          </MainText>
        )}
      </SpacedRow>
      <SpacedRow gap={2}>
        {exercise.tiers.map((t, i) => {
          let displayValue = false;
          if (exercise.category === 3) {
            const minutes = parseInt(t.value / 60);
            const seconds = t.value % 60;
            const convertedMinutes = minutes < 10 ? "0" + minutes : minutes;
            const convertedSeconds =
              parseInt(seconds) < 10
                ? "0" + parseInt(seconds)
                : parseInt(seconds);
            displayValue = convertedMinutes + ":" + convertedSeconds;
          }
          return (
            <MedalCard
              key={t.tier}
              tier={t.tier}
              value={t.value}
              overide={displayValue}
              unit={
                exercise.category === 3
                  ? "min"
                  : exercise.category === 2
                    ? "reps"
                    : "kg"
              }
              pb={pb}
              lowerBetter={exercise.category === 3}
            />
          );
        })}
      </SpacedRow>
      {!allDone && pb !== 0 && nextVal !== null && (
        <Stack gap={1}>
          <SpacedRow alignItems={"center"}>
            <MainText
              fontSize={"0.8rem"}
              sx={{ color: Theme.palette.muted + " !important" }}
            >
              Next milestone
            </MainText>
            <MainText
              fontSize={"0.8rem"}
              fontWeight={700}
              sx={{ color: catColor + " !important" }}
            >
              {pct}%
            </MainText>
          </SpacedRow>
          <ProgressBar pct={pct} color={catColor} />
        </Stack>
      )}
      {allDone && (
        <CenteredRow gap={1} alignSelf={"center"}>
          <MainText>🎉</MainText>
          <MainText
            fontWeight={600}
            sx={{ color: Theme.palette.orange + "!important " }}
          >
            All milestones unlocked!
          </MainText>
        </CenteredRow>
      )}
    </Stack>
  );
};

function progressToNext(pb, tiers, lowerBetter = false) {
  if (!pb) return { pct: 0, nextVal: null };
  const locked = tiers.filter((t) => !isUnlocked(pb, t.value, lowerBetter));
  if (locked.length === 0) return { pct: 100, nextVal: null };
  const next = locked[0];
  const prev = tiers.filter((t) => isUnlocked(pb, t.value, lowerBetter)).pop();
  const prevVal = prev ? prev.value : lowerBetter ? next.value * 2 : 0;
  if (lowerBetter) {
    const total = prevVal - next.value;
    const done = prevVal - pb;
    return {
      pct: Math.min(100, Math.round((done / total) * 100)),
      nextVal: next.value,
    };
  }
  const total = next.value - prevVal;
  const done = pb - prevVal;
  return {
    pct: Math.min(100, Math.round((done / total) * 100)),
    nextVal: next.value,
  };
}

const MedalCard = ({ tier, pb, lowerBetter = false, value, overide, unit }) => {
  const t = TIERS[tier];
  const unlocked = isUnlocked(pb, value, lowerBetter);
  return (
    <Stack
      height={unlocked ? "25vh" : "23vh"}
      flex={1}
      sx={{
        border: 1,
        borderRadius: "12px",
        borderColor: unlocked ? t.color : Theme.palette.border,
        background: Theme.palette.card,
      }}
    >
      <AllCenteredColumn height={"15vh"}>
        <AllCenteredColumn
          width={60}
          height={60}
          borderRadius={"50%"}
          sx={{ background: unlocked ? t.color : Theme.palette.dim }}
        >
          <span style={{ fontSize: "2rem", opacity: unlocked ? 1 : 0.5 }}>
            🏆
          </span>
        </AllCenteredColumn>
      </AllCenteredColumn>
      <AllCenteredColumn
        height={"3vh"}
        sx={{ background: unlocked && "black" }}
      >
        {unlocked && (
          <Typography
            sx={{
              fontSize: "0.6rem",
              color: t.color,
              textTransform: "uppercase",
              letterSpacing: "1.4px",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 700,
            }}
          >
            {t.label}
          </Typography>
        )}
      </AllCenteredColumn>
      <AllCenteredRow
        height={"7vh"}
        sx={{
          background: unlocked ? t.color : Theme.palette.dim,
          borderBottomRightRadius: "12px",
          borderBottomLeftRadius: "12px",
        }}
      >
        <MainText
          fontWeight={700}
          sx={{
            color: Theme.palette.card + " !important",
          }}
        >
          {overide || value}
        </MainText>
        <MainText
          mt={0.4}
          ml={0.3}
          fontSize={"0.8rem"}
          sx={{
            color: Theme.palette.card + " !important",
          }}
        >
          {unit}
        </MainText>
      </AllCenteredRow>
    </Stack>
  );
};

const ProgressBar = ({ color, pct }) => {
  return (
    <Stack gap={1} width={"100%"} position={"relative"} alignSelf={"center"}>
      <Box
        sx={{
          height: 4,
          background: Theme.palette.card,
          border: 1,
          borderColor: color + "22",
          borderRadius: 99,
        }}
      ></Box>
      <Box
        sx={{
          position: "absolute",
          height: 4,
          background: color,
          width: pct + "%",
          border: 1,
          borderColor: color + "22",
          borderRadius: 99,
        }}
      ></Box>
    </Stack>
  );
};
